
/**
 * Network Guard - Interceptor global para bloquear llamadas no autorizadas a /api-dev
 *
 * REGLA: /api-dev SOLO para upload/delete de archivos pesados.
 * Todo lo demás debe usar Supabase.
 */

interface NetworkLogEntry {
  timestamp: string;
  method: string;
  url: string;
  allowed: boolean;
  blocked: boolean;
  stack?: string;
}

type FetchInput = string | URL | { url: string };
type FetchOptions = Record<string, unknown>;

class NetworkGuard {
  private log: NetworkLogEntry[] = [];
  private readonly MAX_LOG_SIZE = 300;

  private readonly ALLOWED_PATHS = [
    '/api-dev/upload',
    '/api-dev/delete',
    '/api-dev/dvd/upload-cover',
    '/api-dev/dvd/upload-frame',
    '/api-dev/jobs/upload-image',
    '/api-dev/files/delete',
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private originalFetch!: (input: any, init?: any) => Promise<Response>;
  private isInitialized = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.originalFetch = window.fetch.bind(window);
    }
  }

  private isAllowedUrl(url: string): boolean {
    if (!url.includes('/api-dev')) {
      return true;
    }
    return this.ALLOWED_PATHS.some((allowedPath) => url.includes(allowedPath));
  }

  private addLogEntry(entry: NetworkLogEntry): void {
    this.log.push(entry);
    if (this.log.length > this.MAX_LOG_SIZE) {
      this.log.shift();
    }
  }

  public getLog(): NetworkLogEntry[] {
    return [...this.log];
  }

  public getStats() {
    const total = this.log.length;
    const blocked = this.log.filter((e) => e.blocked).length;
    const allowedApiDev = this.log.filter(
      (e) => e.allowed && e.url.includes('/api-dev')
    ).length;
    return { total, blocked, allowedApiDev };
  }

  public clearLog(): void {
    this.log = [];
  }

  private resolveUrl(input: FetchInput): string {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return (input as { url: string }).url ?? '';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private createFetchInterceptor(): (input: any, init?: any) => Promise<Response> {
    return async (input: FetchInput, init?: FetchOptions): Promise<Response> => {
      const url = this.resolveUrl(input);
      const method = ((init?.method as string) ?? 'GET').toUpperCase();
      const allowed = this.isAllowedUrl(url);
      const blocked = !allowed;

      let stack: string | undefined;
      try {
        throw new Error();
      } catch (e) {
        stack = (e as Error).stack?.split('\n').slice(2, 5).join('\n');
      }

      this.addLogEntry({ timestamp: new Date().toISOString(), method, url, allowed, blocked, stack });

      if (blocked) {
        const errorMsg = `BLOCKED: /api-dev is files-only. Replace with Supabase.\nURL: ${url}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      return this.originalFetch(input, init);
    };
  }

  private setupAxiosInterceptor(): void {
    if (typeof window === 'undefined') return;
    const win = window as Record<string, unknown>;
    const axios = win['axios'] as
      | { interceptors?: { request?: { use: (fn: (c: unknown) => unknown, e: (err: unknown) => unknown) => void } } }
      | undefined;
    if (!axios?.interceptors?.request) return;

    axios.interceptors.request.use(
      (config: unknown) => {
        const cfg = config as Record<string, unknown>;
        const url = (cfg['url'] as string) || '';
        const method = ((cfg['method'] as string) || 'GET').toUpperCase();
        const allowed = this.isAllowedUrl(url);
        const blocked = !allowed;

        let stack: string | undefined;
        try {
          throw new Error();
        } catch (e) {
          stack = (e as Error).stack?.split('\n').slice(2, 5).join('\n');
        }

        this.addLogEntry({ timestamp: new Date().toISOString(), method, url, allowed, blocked, stack });

        if (blocked) {
          const errorMsg = `BLOCKED: /api-dev is files-only. Replace with Supabase.\nURL: ${url}`;
          console.error(errorMsg);
          return Promise.reject(new Error(errorMsg));
        }

        return config;
      },
      (error: unknown) => Promise.reject(error)
    );
  }

  public initialize(): void {
    if (this.isInitialized) {
      console.warn('Network Guard already initialized');
      return;
    }

    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.fetch = this.createFetchInterceptor() as any;
      this.setupAxiosInterceptor();
      this.isInitialized = true;
      console.log('Network Guard initialized - /api-dev restricted to file operations only');
    }
  }
}

const networkGuard = new NetworkGuard();

export default networkGuard;

export const getNetworkLog = () => networkGuard.getLog();
export const getNetworkStats = () => networkGuard.getStats();
export const clearNetworkLog = () => networkGuard.clearLog();
